import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import {
  Box,
  P,
  IconContainer,
  H3,
  Tabs,
  NavLink,
  Icon,
  Chip,
} from '@allied-solutions/affinity'
import {
  ResourceSolid,
  Shield01,
  Shield02,
} from '@allied-solutions/affinity-react-icons/standard'
import { DashboardTileStatusCardDetailed } from './DashboardTileStatusCardDetailed'
import { DashboardTileCoverageRequirementsRow } from './DashboardTileCoverageRequirementsRow'
import { useWindowWidth } from '../../hooks'
import {
  getSingleAccountChipCount,
  getSingleAccountChipVariant,
} from '../../hooks/useGetCoverageRequirements'
import { theme } from '../../theme'
import { getRequiredCoverageTypeLabel } from '../../utils/requiredCoverageTypeLabels'

const TabLabel = styled.span`
  min-width: 0;
  white-space: normal;

  ${({ theme: t }) => t.mediaQueries.md} {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const StyledNav = styled(Tabs.Nav)`
  display: flex;
  flex-wrap: nowrap;
  padding-bottom: ${props => props.theme.space['xl']};
  border-bottom: none;

  &&& li + li {
    margin-left: 0;
  }
`

const StyledNavItem = styled(Tabs.NavItem)`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;

  && {
    margin: 0;
    margin-left: 0;
  }

  ${NavLink} {
    padding: ${props => `${props.theme.space['lg']}`};
    border-radius: ${props => props.theme.radii[1]};
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    gap: ${props => props.theme.space['md']};

    &:after {
      display: none;
    }
  }
  ${Icon} {
    color: ${props => props.theme.colors.icons.gray};
  }

  ${props =>
    props.active &&
    css`
      ${NavLink} {
        background-color: ${props => props.theme.colors.background.grayMuted};
        color: ${props => props.theme.colors.text.brand};

        & ${Icon} {
          color: ${props => props.theme.colors.icons.brand};
        }
      }
    `}
`

const IconWithBadgeWrapper = styled.span`
  position: relative;
  display: inline-flex;
`

const ErrorBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors?.error};
  border: 2px solid white;
  box-sizing: border-box;
`

const StyledMobileNavItem = styled(Tabs.NavItem)`
  flex: 1;
  min-width: 0;

  ${NavLink} {
    padding: ${props => `${props.theme.space['2xl']}`};
    border-radius: ${props => props.theme.radii[1]};
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: ${props => props.theme.space['md']};
    text-align: center;

    &:after {
      display: none;
    }
  }

  ${Icon} {
    color: ${props => props.theme.colors.icons.gray};
  }

  ${props =>
    props.active &&
    css`
      ${NavLink} {
        background-color: ${props => props.theme.colors.background.grayMuted};
        color: ${props => props.theme.colors.text.brand};

        & ${Icon} {
          color: ${props => props.theme.colors.icons.brand};
        }
      }
    `}
`

const StyledTabsContent = styled(Tabs.Content)`
  padding: ${props => props.theme.space['2xl']} 0;
`

// Resolving issue of page jumping when switching tabs
const preserveScrollAfterTabChange = () => {
  const x = window.scrollX
  const y = window.scrollY
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (window.scrollX !== x || window.scrollY !== y) {
        window.scrollTo(x, y)
      }
    })
  })
}

export const DashboardTileCoverageRequirements = ({
  onOpenHelp,
  coverageRequirementsData,
  initialAccountSlug = '0',
  unmetRequirementsCount,
  openUploadModal,
  onActiveCoverageAccountChange,
}) => {
  const requirementsArray = Array.isArray(coverageRequirementsData)
    ? coverageRequirementsData
    : []
  const isMobile = useWindowWidth() < Number.parseInt(theme.breakpoints[1])

  const handleCoverageTabClick = (accountIndex, referenceId) => {
    preserveScrollAfterTabChange()
    onActiveCoverageAccountChange?.(accountIndex, referenceId)
  }

  return (
    <>
      <Box display="flex" alignItems="center" gap="lg" mb="4xl">
        <IconContainer icon={Shield02} color="brand" />
        <H3 typeStyle="titleSmall" textAlign="left" fontWeight="700" mb="0">
          Coverage Requirements
        </H3>
        {isMobile && unmetRequirementsCount > 0 && (
          <Chip label={unmetRequirementsCount} rounded variant="gray" />
        )}
      </Box>
      <Box>
        <Box
          p="4xl"
          border="1px solid"
          borderColor="border.defaultLight"
          borderRadius="2"
          className="loan-information-box"
        >
          <Tabs idPrefix="Coverage__Requirements__Tabs">
            {({ active }) => (
              <>
                <StyledNav>
                  {requirementsArray.map((account, index) => {
                    const slug = String(index)
                    const accountChipCount = getSingleAccountChipCount(account)
                    const chipVariant = getSingleAccountChipVariant(account)
                    const coverageTypeLabel = getRequiredCoverageTypeLabel(
                      account.RequiredCoverageType
                    )
                    return isMobile ? (
                      <StyledMobileNavItem
                        key={account.ReferenceID ?? slug}
                        active={active === slug}
                        slug={slug}
                        typeStyle="labelMedium"
                        onClick={() =>
                          handleCoverageTabClick(index, account.ReferenceID)
                        }
                      >
                        <IconWithBadgeWrapper>
                          <Icon src={Shield01} />
                          {accountChipCount > 0 && <ErrorBadge />}
                        </IconWithBadgeWrapper>
                        <TabLabel title={coverageTypeLabel}>
                          {coverageTypeLabel}
                        </TabLabel>
                      </StyledMobileNavItem>
                    ) : (
                      <StyledNavItem
                        key={account.ReferenceID ?? slug}
                        active={active === slug}
                        slug={slug}
                        typeStyle="labelMedium"
                        onClick={() =>
                          handleCoverageTabClick(index, account.ReferenceID)
                        }
                      >
                        <TabLabel title={coverageTypeLabel}>
                          {coverageTypeLabel}
                        </TabLabel>
                        {accountChipCount > 0 && (
                          <Chip
                            label={accountChipCount}
                            rounded
                            variant={chipVariant}
                          />
                        )}
                      </StyledNavItem>
                    )
                  })}
                </StyledNav>
                <StyledTabsContent>
                  {requirementsArray.map((account, index) => {
                    const slug = String(index)
                    const accountErrorCount =
                      account.CoverageRequirements?.filter(
                        req => req.Status === false
                      ).length ?? 0
                    return (
                      <Tabs.Pane
                        key={account.ReferenceID ?? slug}
                        slug={slug}
                        active={
                          String(index) === String(initialAccountSlug ?? '0')
                        }
                      >
                        <DashboardTileStatusCardDetailed
                          accountStatusDetails={account.accountStatusDetails}
                          errorCount={accountErrorCount}
                          openUploadModal={openUploadModal}
                          referenceId={account.ReferenceID}
                          onOpenHelp={onOpenHelp}
                        />
                        <Box
                          display="flex"
                          alignItems="center"
                          gap="md"
                          mt="4xl"
                          mb="sm"
                        >
                          <Icon src={ResourceSolid} color="gray" size="sm" />
                          <P typeStyle="caption" color="text.secondary" mb="0">
                            Click each row to learn more.
                          </P>
                        </Box>
                        {(account.CoverageRequirements ?? []).map(
                          (requirement, reqIndex) => (
                            <DashboardTileCoverageRequirementsRow
                              key={requirement.Label}
                              {...requirement}
                              accountStatusDetails={
                                account.accountStatusDetails
                              }
                              isLast={
                                reqIndex ===
                                (account.CoverageRequirements?.length ?? 0) - 1
                              }
                            />
                          )
                        )}
                      </Tabs.Pane>
                    )
                  })}
                </StyledTabsContent>
              </>
            )}
          </Tabs>
        </Box>
      </Box>
    </>
  )
}

DashboardTileCoverageRequirements.propTypes = {
  coverageRequirementsData: PropTypes.arrayOf(PropTypes.object),
  initialAccountSlug: PropTypes.string,
  unmetRequirementsCount: PropTypes.number,
  openUploadModal: PropTypes.func,
  onActiveCoverageAccountChange: PropTypes.func,
  onOpenHelp: PropTypes.func,
}
